import { Order } from '@/components/collapsible-table';
import { DocumentDto } from '@/models';
import {
  Program,
  ProgramFund,
  ProgramModelFactsheet,
  ProgramPlanDto,
  ProgramRequest
} from '@/models/ops/investments/Program.model';
import { ReEnrollmentType } from '@/routes/ops/investments/ProgramDetailsAddPlan/ReEnrollmentModal.component';
import { ChangeMapping } from '@/routes/ops/investments/re-enrollment/InvestmentAutocomplete.component';
import { SlotMetaData } from '@/routes/plans/plan-detail/PlanDocumentsTab/PlanDocumentsTab.component';
import ApiService from '@/services/Api.service';

type ProgramListDto = { data: Program[]; meta: { count: number } };

type ProgramPlansListDto = { data: ProgramPlanDto[]; meta: { count: number } };

export type InvestmentOption =
  | 'targetSeries'
  | 'riskSeries'
  | 'goalSeries'
  | 'fundLineup'
  | '401kManager'
  | 'tiaaNli';

export type FundChangesPreview = {
  isRemoved?: boolean;
  oldFund: string;
  newFund: string;
  tradeDate: string;
  investmentOptions: InvestmentOption[];
};

export type SponsorPlan = {
  sponsorPlanId: number;
  sponsorPlanName: string;
};

export type FundChangePreview = {
  fundChange: FundChangesPreview;
  plansAffected: SponsorPlan[];
};

export type FundChanges = FundChangesPreview & {
  fundChangeId: number;
};

export type PostProgramChangeReenrollmentBody = {
  reenrollmentType: ReEnrollmentType;
  jiraTicket: string;
  effectiveDate: string;
  mappings?: ChangeMapping[];
};

type ProgramChangeStatus = 'pending' | 'approved' | 'rejected' | 'canceled';

export type ProgramChange = {
  programChangeId: number;
  reEnrollmentType: ReEnrollmentType;
  sponsorPlanId: number;
  sponsorPlanName: string;
  oldProgramId?: number;
  oldProgramName?: string;
  newProgramId: number;
  newProgramName: string;
  programMappings?: [
    {
      newType: string;
      oldModelId: number;
      oldType: string;
    }
  ];
  programChangeCreatedAt: string;
  programChangeCreatedBy: {
    authRole: string;
    id: string;
    idType: string;
  };
  opsApprovalId: number;
  opsEventType: string;
  jiraTicket: string;
  approvalCreatedAt: string;
  approvedAt?: string;
  approvedBy?: {
    authRole: string;
    id: string;
    idType: string;
  };
  canceledAt?: string;
  canceledBy?: {
    authRole: string;
    id: string;
    idType: string;
  };
  status: ProgramChangeStatus;
  effectiveDate: string;
};

type OpsEventType = 'Re-Enrollment';

export type ProgramChangeWithApproval = {
  /** @format date-time */
  approvalCreatedAt: string;
  approvalCreatedBy: {
    authRole: string;
    id: string;
    idType: string;
  };
  /** @format date-time */
  approvedAt?: string;
  approvedBy?: string;
  /** @format date-time */
  canceledAt?: string;
  canceledBy?: string;
  /** @format date-time */
  createdAt: string;
  createdBy: {
    authRole: string;
    id: string;
    idType: string;
  };
  effectiveDate: string;
  jiraTicket: string;
  /** @format int64 */
  newProgramId: number;
  /** @format int64 */
  oldProgramId: number;
  /** @format int64 */
  opsApprovalId?: number;
  opsEventType: OpsEventType;
  /** @format int64 */
  programChangeId: number;
  programMappings?: [
    {
      newType: string;
      oldModelId: number;
      oldType: string;
    }
  ];
  reEnrollmentType: ReEnrollmentType;
  /** @format int64 */
  sponsorPlanId: number;
  sponsorPlanName: string;
  status: ProgramChangeStatus;
};

class ProgramService {
  static async getProgramsPage(
    pageNumber: number,
    pageSize: number,
    query: string,
    orderBy: string,
    order: Order
  ): Promise<ProgramListDto> {
    const sort = (order === 'asc' ? '' : '-').concat(orderBy);
    return ApiService.getJson('/investments/program', {
      pageNumber,
      pageSize,
      query,
      sort
    }) as Promise<ProgramListDto>;
  }

  static async getProgramsPageWithPlanCount(
    pageNumber: number,
    pageSize: number,
    query: string,
    orderBy: string,
    order: Order
  ): Promise<ProgramListDto> {
    const sort = (order === 'asc' ? '' : '-').concat(orderBy);
    return ApiService.getJson('/investments/program', {
      include: 'planCount',
      pageNumber,
      pageSize,
      query,
      sort
    }) as Promise<ProgramListDto>;
  }

  static async getProgram(programId: number): Promise<Program> {
    return ApiService.getJson(`/investments/program/${programId}`);
  }

  static async postProgram(data: ProgramRequest): Promise<Program> {
    return ApiService.postJson(`investments/program`, data);
  }

  static async putProgram(
    programId: number,
    data: ProgramRequest
  ): Promise<Program> {
    return ApiService.putJson(`investments/program/${programId}`, data);
  }

  static async getModelProgramsPage(
    investmentOptionId: number,
    modelType: string,
    pageNumber: number,
    pageSize: number,
    query: string,
    orderBy: string,
    order: Order
  ): Promise<ProgramListDto> {
    const sort = (order === 'asc' ? '' : '-').concat(orderBy);
    let modelTypeParameter;
    switch (modelType) {
      case 'target':
        modelTypeParameter = { targetDateSeriesId: investmentOptionId };
        break;
      case 'risk':
        modelTypeParameter = { riskSeriesId: investmentOptionId };
        break;
      case 'fundLineup':
        modelTypeParameter = { fundLineupId: investmentOptionId };
        break;
      case 'goal':
        modelTypeParameter = { goalSeriesId: investmentOptionId };
        break;
      default:
        modelTypeParameter = {};
    }
    return ApiService.getJson(`/investments/program`, {
      pageNumber,
      pageSize,
      query,
      sort,
      ...modelTypeParameter
    }) as Promise<ProgramListDto>;
  }

  static async getFundChanges(
    programId: number
  ): Promise<{ data: FundChanges[] }> {
    return ApiService.getJson(`/investments/program/${programId}/fund-change`);
  }

  static async createFundChangePreview(
    programId: number,
    fundChangesPreview: FundChangesPreview[]
  ): Promise<FundChangePreview[]> {
    return ApiService.postJson(
      `/investments/program/${programId}/fund-change/preview`,
      fundChangesPreview
    );
  }

  static async createFundChange(
    programId: number,
    FundChangesPreview: FundChangesPreview
  ): Promise<FundChanges> {
    return ApiService.postJson(
      `/investments/program/${programId}/fund-change`,
      FundChangesPreview
    );
  }

  static async getAllProgramDocuments(
    programId: number
  ): Promise<SlotMetaData[]> {
    return ApiService.getJson(`investments/program/${programId}/documents/all`);
  }

  static async getDocumentForProgramId(
    programId: string | number,
    documentId: string | number
  ): Promise<DocumentDto> {
    return ApiService.getJson(
      `investments/program/${programId}/document/${documentId}`
    );
  }

  static async getProgramModelFactsheets(
    programId: number
  ): Promise<ProgramModelFactsheet[]> {
    return ApiService.getJson(
      `investments/program/${programId}/model-factsheets`
    );
  }

  static async uploadProgramDocument(
    programId: number,
    documentData: FormData
  ): Promise<any> {
    return ApiService.postFormData(
      `investments/program/${programId}/document`,
      documentData
    );
  }

  static async deleteProgramDocument(
    programId: number,
    documentId: string | number
  ): Promise<any> {
    return ApiService.deleteJson(
      `investments/program/${programId}/document/${documentId}`
    );
  }

  static async getProgramPlans(
    programId: number,
    pageNumber: number,
    pageSize: number,
    search: string,
    orderBy: string,
    order: Order,
    associated?: boolean
  ): Promise<ProgramPlansListDto> {
    const sort = (order === 'asc' ? '' : '-').concat(orderBy);
    return ApiService.getJson(`investments/program/${programId}/plans`, {
      associated,
      pageNumber,
      pageSize,
      search,
      sort
    });
  }

  static async getProgramFunds(programId: number): Promise<ProgramFund[]> {
    return ApiService.getJson(`/investments/program/${programId}/fund`);
  }

  static async postProgramChangeReenrollment(
    programId: number,
    planId: number,
    body: PostProgramChangeReenrollmentBody
  ): Promise<{ programChangeId: number }> {
    return ApiService.postJson(
      `/investments/program/${programId}/plans/${planId}/re-enrollment`,
      body
    );
  }

  static async getProgramChangesWithApprovals(
    programId: number
  ): Promise<{ changes: ProgramChange[] }> {
    return ApiService.getJson(
      `/investments/program/${programId}/program-change/re-enrollment`
    );
  }

  static async getProgramChangeReenrollmentById(
    programChangeId: number,
    planId: number
  ): Promise<ProgramChangeWithApproval> {
    return ApiService.getJson(
      `/investments/program/plans/${planId}/program-change/re-enrollment/${programChangeId}`
    );
  }
}

export type { ProgramListDto, ProgramPlansListDto };
export { ProgramService };
